import { Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import * as AuthSelectors from '../../auth/store/auth.selectors';
import { RoleNames } from '../api/types/enums';
import { PermissionService } from '../services/permission.service';

/**
 * Structural directive that conditionally includes a template based on Owner access.
 * This directive checks if the user has Owner access for the specified roles.
 *
 * Usage:
 * <button *appHasOwner="RoleNames.products">Delete Product</button>
 * <div *appHasOwner="[RoleNames.products, RoleNames.pricing]">Owner access to products OR pricing</div>
 * <div *appHasOwner="[RoleNames.products, RoleNames.pricing]; requireAll: true">Owner access to products AND pricing</div>
 */
@Directive({
    selector: '[appHasOwner]',
    standalone: true
})
export class HasOwnerDirective implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private hasView = false;
  private currentRoles: RoleNames | RoleNames[] = [];
  private requireAll = false;

  @Input() set appHasOwner(roles: RoleNames | RoleNames[]) {
    this.currentRoles = roles;
    this.updateView();
  }

  @Input() set appHasOwnerRequireAll(require: boolean) {
    this.requireAll = require;
    this.updateView();
  }

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private store: Store,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    // For static roles, we only need to check once after initialization
    // But keep subscription for backward compatibility
    this.store
      .select(AuthSelectors.selectAuthRoles)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateView();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateView(): void {
    if (!this.currentRoles) {
      this.showTemplate();
      return;
    }

    // Check for Owner access specifically
    const hasOwnerAccess = this.requireAll
      ? this.permissionService.hasAllOwnerAccess(this.currentRoles)
      : this.permissionService.hasOwnerAccess(this.currentRoles);

    if (hasOwnerAccess && !this.hasView) {
      this.showTemplate();
    } else if (!hasOwnerAccess && this.hasView) {
      this.hideTemplate();
    }
  }

  private showTemplate(): void {
    if (!this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    }
  }

  private hideTemplate(): void {
    if (this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}
